import {
    IExpressionResolver, IExpressionResolution, ResolutionErrorKind,
    successfulExpressionResolution, failedExpressionResolution, IResolutionError
} from 'app/model/expressions/expression-resolution';
import { Metamodel } from 'app/model/base/model';
import { ExpressionCategory, Expression, ScalarExpression } from './expression';
import { Owned } from 'app/model/base/named';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Dataset } from 'app/model/dataset/dataset';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';

/**
 * A resolver capable of parsing trigger parameter such as: @pipeline().TriggerEvent.FilePath, ...
 */
export class TriggerParameterResolver implements IExpressionResolver {
    /**
     * Initializes a new instance of the TriggerParameterResolver class.
     * @param _registeredExpressions The registered expressions to search for trigger parameters within.
     */
    public constructor(private _registeredExpressions: Expression[]) {
    }

    public isOfType(expression: string, ignoreStart: boolean = false): boolean {
        expression = expression ? expression.trim() : '';
        return expression.startsWith(ignoreStart ? 'pipeline().TriggerEvent' : '@pipeline().TriggerEvent') ||
            expression.startsWith(ignoreStart ? 'pipeline()?.TriggerEvent' : '@pipeline()?.TriggerEvent');
    }

    public resolve(expression: string, ignoreStart: boolean = false): IExpressionResolution {
        if (this.isOfType(expression, ignoreStart)) {
            const cleanedExpression = this.cleanExpression(expression, ignoreStart);
            const triggerParameterMatch = this._registeredExpressions?.find(
                (exp) => exp.category === ExpressionCategory.TriggerParameter && stringsAreEqualCaseInsensitive(exp.syntax, cleanedExpression)) as ScalarExpression;

            // These three are special unofficial variables. The official counterparts have the questionmark - e.g pipeline()?.TriggerEvent
            // We will show a warning to the user about their usage
            if (cleanedExpression.includes('pipeline().TriggerEvent') ||
                cleanedExpression.includes('TriggerEvent.Type') ||
                cleanedExpression.includes('TriggerEvent.Source') ||
                cleanedExpression.includes('TriggerEvent.Subject') ||
                cleanedExpression.includes('TriggerEvent.FileName') ||
                cleanedExpression.includes('TriggerEvent.FolderPath')) {
                return failedExpressionResolution(expression, {
                    kind: ResolutionErrorKind.TriggerEventWarning,
                    details: { triggerParameterName: cleanedExpression.replace('@', '') }
                }, undefined, undefined, true /*treatAsWarning*/);
            }

            return triggerParameterMatch
                ? successfulExpressionResolution(expression, triggerParameterMatch)
                : failedExpressionResolution(expression, { kind: ResolutionErrorKind.ParameterNotFound, details: { parameterName: cleanedExpression.replace('@', '') } });
        } else {
            return failedExpressionResolution(expression, { kind: ResolutionErrorKind.Unrecognized, details: expression });
        }
    }

    public checkApplicability(entity: Owned): { isApplicable: boolean, error?: IResolutionError } {
        return { isApplicable: Metamodel.modelOf(entity.concept) === Pipeline.MODEL || entity.concept instanceof Dataset };
    }

    private cleanExpression(expression: string, ignoreStart: boolean): string {
        expression = expression ? expression.trim() : '';
        expression = expression && !ignoreStart ? expression.slice(1) : expression; // take out the leading @ if this is a top level expression

        return expression;
    }
}
